import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useConnection } from "../context/ConnectionContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Database,
  Server,
  Table2,
  Columns,
  ChevronRight,
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  Download,
  ChevronLeft,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  AlertCircle,
  Crown,
  Zap,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const DatabaseExplorerPage = () => {
  const { getAuthHeader, updateUserCredits } = useAuth();
  const { selectedConnection } = useConnection();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [columns, setColumns] = useState([]);
  const [tableData, setTableData] = useState({ columns: [], rows: [], total_count: 0 });
  const [loading, setLoading] = useState({ tables: false, columns: false, data: false });
  const [expandedTables, setExpandedTables] = useState({});
  const [pagination, setPagination] = useState({ limit: 50, offset: 0 });
  const [searchTerm, setSearchTerm] = useState("");

  // CRUD State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Field editing state
  const [fieldEditDialogOpen, setFieldEditDialogOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [fieldNewType, setFieldNewType] = useState("");
  const [savingField, setSavingField] = useState(false);

  // Add field state
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState("");
  const [newFieldDefault, setNewFieldDefault] = useState("");
  const [newFieldNullable, setNewFieldNullable] = useState(true);
  const [savingNewField, setSavingNewField] = useState(false);

  // Delete field state
  const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [deletingField, setDeletingField] = useState(false);

  // Load tables when connection changes
  useEffect(() => {
    if (selectedConnection) {
      setSelectedTable(null);
      setColumns([]);
      setTableData({ columns: [], rows: [], total_count: 0 });
      setExpandedTables({});
      setSelectedRows(new Set());
      fetchTables(selectedConnection.id);
    } else {
      setTables([]);
      setSelectedTable(null);
    }
  }, [selectedConnection]);

  const fetchTables = async (connId) => {
    setLoading(prev => ({ ...prev, tables: true }));
    try {
      const response = await axios.get(`${API_URL}/connections/${connId}/tables`, {
        headers: getAuthHeader()
      });
      setTables(response.data.tables);
    } catch (error) {
      toast.error("Failed to fetch tables");
      setTables([]);
    } finally {
      setLoading(prev => ({ ...prev, tables: false }));
    }
  };

  const fetchColumns = async (connId, tableName) => {
    setLoading(prev => ({ ...prev, columns: true }));
    try {
      const response = await axios.get(`${API_URL}/connections/${connId}/tables/${tableName}/columns`, {
        headers: getAuthHeader()
      });
      setColumns(response.data.columns);
      return response.data.columns;
    } catch (error) {
      toast.error("Failed to fetch columns");
      setColumns([]);
      return [];
    } finally {
      setLoading(prev => ({ ...prev, columns: false }));
    }
  };

  const fetchTableData = async (connId, tableName, limit = 50, offset = 0) => {
    setLoading(prev => ({ ...prev, data: true }));
    try {
      const response = await axios.get(
        `${API_URL}/connections/${connId}/tables/${tableName}/data?limit=${limit}&offset=${offset}`,
        { headers: getAuthHeader() }
      );
      setTableData(response.data);
      setPagination({ limit, offset });
      setSelectedRows(new Set());
      // Update credits in sidebar if data was returned
      if (response.data.credits_remaining !== undefined) {
        updateUserCredits(response.data.credits_remaining);
      }
    } catch (error) {
      toast.error("Failed to fetch table data");
      setTableData({ columns: [], rows: [], total_count: 0 });
    } finally {
      setLoading(prev => ({ ...prev, data: false }));
    }
  };

  const handleTableSelect = async (tableName) => {
    if (!selectedConnection) return;
    setSelectedTable(tableName);
    setPagination({ limit: 50, offset: 0 });
    setSelectedRows(new Set());
    await fetchColumns(selectedConnection.id, tableName);
    await fetchTableData(selectedConnection.id, tableName);
  };

  const toggleTableExpand = (tableName) => {
    if (!selectedConnection) return;
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
    if (!expandedTables[tableName]) {
      fetchColumns(selectedConnection.id, tableName);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.post(`${API_URL}/export`, {
        format,
        data: { columns: tableData.columns, rows: tableData.rows }
      }, { headers: getAuthHeader() });

      const blob = new Blob([response.data.content], { type: response.data.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedTable}_export.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // CREATE - Open dialog with empty form
  const handleOpenCreate = async () => {
    let cols = columns;
    if (cols.length === 0) {
      cols = await fetchColumns(selectedConnection.id, selectedTable);
    }
    // Initialize form with empty values for each column (except _id for MongoDB)
    const initialData = {};
    cols.forEach(col => {
      if (col.name !== '_id') {
        initialData[col.name] = '';
      }
    });
    setFormData(initialData);
    setCreateDialogOpen(true);
  };

  // CREATE - Submit new record
  const handleCreate = async () => {
    setSaving(true);
    try {
      // Parse JSON values if needed
      const parsedData = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value === '') {
          return; // Skip empty values
        }
        // Try to parse as JSON for objects/arrays
        try {
          if (value.startsWith('{') || value.startsWith('[')) {
            parsedData[key] = JSON.parse(value);
          } else if (value === 'true' || value === 'false') {
            parsedData[key] = value === 'true';
          } else if (!isNaN(value) && value.trim() !== '') {
            parsedData[key] = Number(value);
          } else {
            parsedData[key] = value;
          }
        } catch {
          parsedData[key] = value;
        }
      });

      await axios.post(
        `${API_URL}/connections/${selectedConnection.id}/tables/${selectedTable}/rows`,
        parsedData,
        { headers: getAuthHeader() }
      );
      toast.success("Record created successfully");
      setCreateDialogOpen(false);
      fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset);
      fetchTables(selectedConnection.id); // Refresh row counts
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create record");
    } finally {
      setSaving(false);
    }
  };

  // EDIT - Open dialog with row data
  const handleOpenEdit = (row, index) => {
    setSelectedRow(row);
    setSelectedRowIndex(index);
    const editData = {};
    Object.keys(row).forEach(key => {
      if (typeof row[key] === 'object' && row[key] !== null) {
        editData[key] = JSON.stringify(row[key], null, 2);
      } else {
        editData[key] = String(row[key] ?? '');
      }
    });
    setFormData(editData);
    setEditDialogOpen(true);
  };

  // EDIT - Submit updated record
  const handleUpdate = async () => {
    setSaving(true);
    try {
      // Determine the primary key field
      const pkField = selectedConnection.db_type === 'mongodb' ? '_id' : 
        columns.find(c => c.is_primary)?.name || columns[0]?.name;
      
      // Build filter using primary key
      const filterData = {};
      filterData[pkField] = selectedRow[pkField];

      // Parse updated values
      const updateData = {};
      Object.keys(formData).forEach(key => {
        if (key === '_id' || key === pkField) return; // Don't update PK
        const value = formData[key];
        try {
          if (value.startsWith('{') || value.startsWith('[')) {
            updateData[key] = JSON.parse(value);
          } else if (value === 'true' || value === 'false') {
            updateData[key] = value === 'true';
          } else if (!isNaN(value) && value.trim() !== '') {
            updateData[key] = Number(value);
          } else {
            updateData[key] = value;
          }
        } catch {
          updateData[key] = value;
        }
      });

      await axios.put(
        `${API_URL}/connections/${selectedConnection.id}/tables/${selectedTable}/rows`,
        { filter_data: filterData, update_data: updateData },
        { headers: getAuthHeader() }
      );
      toast.success("Record updated successfully");
      setEditDialogOpen(false);
      fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update record");
    } finally {
      setSaving(false);
    }
  };

  // DELETE - Open confirmation dialog
  const handleOpenDelete = (row, index) => {
    setSelectedRow(row);
    setSelectedRowIndex(index);
    setDeleteDialogOpen(true);
  };

  // DELETE - Confirm deletion
  const handleDelete = async () => {
    setSaving(true);
    try {
      // Determine the primary key field
      const pkField = selectedConnection.db_type === 'mongodb' ? '_id' : 
        columns.find(c => c.is_primary)?.name || columns[0]?.name;
      
      const filterData = {};
      filterData[pkField] = selectedRow[pkField];

      await axios.delete(
        `${API_URL}/connections/${selectedConnection.id}/tables/${selectedTable}/rows`,
        { 
          headers: getAuthHeader(),
          data: filterData
        }
      );
      toast.success("Record deleted successfully");
      setDeleteDialogOpen(false);
      fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset);
      fetchTables(selectedConnection.id); // Refresh row counts
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete record");
    } finally {
      setSaving(false);
    }
  };

  // BULK DELETE
  const handleBulkDelete = async () => {
    setSaving(true);
    try {
      const pkField = selectedConnection.db_type === 'mongodb' ? '_id' : 
        columns.find(c => c.is_primary)?.name || columns[0]?.name;
      
      const selectedRowsArray = Array.from(selectedRows);
      let deletedCount = 0;
      
      for (const idx of selectedRowsArray) {
        const row = tableData.rows[idx];
        const filterData = {};
        filterData[pkField] = row[pkField];
        
        try {
          await axios.delete(
            `${API_URL}/connections/${selectedConnection.id}/tables/${selectedTable}/rows`,
            { 
              headers: getAuthHeader(),
              data: filterData
            }
          );
          deletedCount++;
        } catch (e) {
          console.error(`Failed to delete row ${idx}:`, e);
        }
      }
      
      toast.success(`Deleted ${deletedCount} record(s)`);
      setBulkDeleteDialogOpen(false);
      setSelectedRows(new Set());
      fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset);
      fetchTables(selectedConnection.id);
    } catch (error) {
      toast.error("Failed to delete records");
    } finally {
      setSaving(false);
    }
  };

  // Toggle row selection
  const toggleRowSelection = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  // Toggle all rows selection
  const toggleAllRows = () => {
    if (selectedRows.size === tableData.rows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(tableData.rows.map((_, i) => i)));
    }
  };

  const filteredTables = tables.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(tableData.total_count / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  // Get editable columns (exclude _id for create)
  const editableColumns = columns.filter(col => col.name !== '_id');
  
  // Handle field click to open type editor
  const handleFieldClick = (col) => {
    setSelectedField(col);
    setFieldNewType(col.data_type);
    setFieldEditDialogOpen(true);
  };
  
  // Available data types based on database type
  const getAvailableDataTypes = () => {
    const dbType = selectedConnection?.db_type?.toLowerCase() || '';
    
    if (dbType.includes('mongo')) {
      return ['String', 'Number', 'Boolean', 'Date', 'ObjectId', 'Array', 'Object', 'Null'];
    } else if (dbType.includes('postgres')) {
      return ['integer', 'bigint', 'smallint', 'serial', 'text', 'varchar', 'char', 'boolean', 'date', 'timestamp', 'timestamptz', 'numeric', 'real', 'double precision', 'json', 'jsonb', 'uuid'];
    } else if (dbType.includes('mysql') || dbType.includes('maria')) {
      return ['INT', 'BIGINT', 'SMALLINT', 'TINYINT', 'VARCHAR', 'TEXT', 'CHAR', 'BOOLEAN', 'DATE', 'DATETIME', 'TIMESTAMP', 'DECIMAL', 'FLOAT', 'DOUBLE', 'JSON', 'BLOB'];
    } else if (dbType.includes('sql') || dbType.includes('mssql')) {
      return ['int', 'bigint', 'smallint', 'tinyint', 'varchar', 'nvarchar', 'text', 'ntext', 'char', 'nchar', 'bit', 'date', 'datetime', 'datetime2', 'decimal', 'numeric', 'float', 'real', 'uniqueidentifier'];
    } else if (dbType.includes('sqlite')) {
      return ['INTEGER', 'TEXT', 'REAL', 'BLOB', 'NUMERIC'];
    } else {
      return ['String', 'Integer', 'Float', 'Boolean', 'Date', 'Text', 'JSON'];
    }
  };
  
  // Save field type change
  const handleSaveFieldType = async () => {
    if (!selectedField || !fieldNewType || fieldNewType === selectedField.data_type) {
      setFieldEditDialogOpen(false);
      return;
    }
    
    setSavingField(true);
    try {
      await axios.post(
        `${API_URL}/connections/${selectedConnection.id}/tables/${selectedTable}/alter-field`,
        { field_name: selectedField.name, new_type: fieldNewType },
        { headers: getAuthHeader() }
      );
      toast.success(`Field '${selectedField.name}' type changed to ${fieldNewType}`);
      setFieldEditDialogOpen(false);
      // Refresh columns and data
      await fetchColumns(selectedConnection.id, selectedTable);
      await fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to alter field type");
    } finally {
      setSavingField(false);
    }
  };

  // Add new field to table
  const handleAddField = async () => {
    if (!newFieldName.trim() || !newFieldType) {
      toast.error("Field name and type are required");
      return;
    }
    
    setSavingNewField(true);
    try {
      await axios.post(
        `${API_URL}/connections/${selectedConnection.id}/tables/${selectedTable}/add-field`,
        {
          field_name: newFieldName.trim(),
          field_type: newFieldType,
          default_value: newFieldDefault || null,
          nullable: newFieldNullable
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Field '${newFieldName}' added successfully`);
      setAddFieldDialogOpen(false);
      setNewFieldName("");
      setNewFieldType("");
      setNewFieldDefault("");
      setNewFieldNullable(true);
      // Refresh columns and data
      await fetchColumns(selectedConnection.id, selectedTable);
      await fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add field");
    } finally {
      setSavingNewField(false);
    }
  };

  // Delete field from table
  const handleDeleteField = async () => {
    if (!fieldToDelete || !selectedConnection || !selectedTable) return;
    setDeletingField(true);
    try {
      await axios.post(
        `${API_URL}/connections/${selectedConnection.id}/tables/${selectedTable}/delete-field`,
        { field_name: fieldToDelete.name },
        { headers: getAuthHeader() }
      );
      toast.success(`Field '${fieldToDelete.name}' deleted`);
      setDeleteFieldDialogOpen(false);
      setFieldToDelete(null);
      await fetchColumns(selectedConnection.id, selectedTable);
      await fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete field");
    } finally {
      setDeletingField(false);
    }
  };

  // Open delete field confirmation
  const handleOpenDeleteField = (col, e) => {
    e?.stopPropagation();
    setFieldToDelete(col);
    setDeleteFieldDialogOpen(true);
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Sidebar - Tree View */}
      <aside className="w-72 border-r border-border bg-card/50 flex flex-col">
        <div className="p-4 border-b border-border space-y-3">
          {/* Show current connection info */}
          {selectedConnection ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg">
              {selectedConnection.db_type === 'mongodb' ? (
                <Database className="w-4 h-4 text-green-500" />
              ) : (
                <Server className="w-4 h-4 text-blue-500" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{selectedConnection.name}</p>
                <p className="text-xs text-muted-foreground">
                  {selectedConnection.db_type === 'mssql' ? 'MS SQL Server' : 'MongoDB'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Select a connection</span>
            </div>
          )}
          
          {selectedConnection && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search tables..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="explorer-search-input"
              />
            </div>
          )}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2">
            {!selectedConnection ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Select a connection from the sidebar
              </div>
            ) : loading.tables ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : filteredTables.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No tables found
              </div>
            ) : (
              <div className="space-y-1">
                {filteredTables.map(table => (
                  <Collapsible
                    key={table.name}
                    open={expandedTables[table.name]}
                    onOpenChange={() => toggleTableExpand(table.name)}
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                          selectedTable === table.name
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted/50 text-foreground'
                        }`}
                        onClick={() => handleTableSelect(table.name)}
                        data-testid={`table-item-${table.name}`}
                      >
                        {expandedTables[table.name] ? (
                          <ChevronDown className="w-4 h-4 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 flex-shrink-0" />
                        )}
                        <Table2 className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate flex-1 text-left">{table.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {table.row_count?.toLocaleString()}
                        </span>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="ml-8 mt-1 space-y-1">
                        {loading.columns && selectedTable === table.name ? (
                          <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Loading columns...
                          </div>
                        ) : (
                          columns.map(col => (
                            <div
                              key={col.name}
                              className="group w-full flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground hover:bg-primary/10 hover:text-primary rounded transition-colors"
                              data-testid={`field-${col.name}`}
                            >
                              <button
                                onClick={() => handleFieldClick(col)}
                                className="flex items-center gap-2 flex-1 min-w-0 text-left"
                              >
                                <Columns className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate flex-1">{col.name}</span>
                                <span className="text-[10px] opacity-60 bg-muted px-1 rounded">{col.data_type}</span>
                                {col.is_primary && (
                                  <span className="text-[10px] bg-primary/20 text-primary px-1 rounded">PK</span>
                                )}
                              </button>
                              {!col.is_primary && (
                                <button
                                  onClick={(e) => handleOpenDeleteField(col, e)}
                                  className="opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity flex-shrink-0"
                                  title={`Delete field ${col.name}`}
                                  data-testid={`delete-field-${col.name}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </aside>

      {/* Main Content - Data View */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            {selectedTable ? (
              <>
                <Table2 className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">{selectedTable}</span>
                <span className="text-sm text-muted-foreground">
                  ({tableData.total_count.toLocaleString()} rows)
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">Select a table to view data</span>
            )}
          </div>
          {selectedTable && (
            <div className="flex items-center gap-2">
              {selectedRows.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  className="gap-1"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete ({selectedRows.size})
                </Button>
              )}
              <Button
                variant="default"
                size="sm"
                onClick={handleOpenCreate}
                className="gap-1"
                data-testid="add-record-btn"
              >
                <Plus className="w-4 h-4" />
                Add Record
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAddFieldDialogOpen(true)}
                className="gap-1"
                data-testid="add-field-btn"
              >
                <Columns className="w-4 h-4" />
                Add Field
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset)}
                data-testid="refresh-data-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
                className="gap-1"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('json')}
                className="gap-1"
                data-testid="export-json-btn"
              >
                <Download className="w-4 h-4" />
                JSON
              </Button>
            </div>
          )}
        </header>

        {/* Data Table */}
        <div className="flex-1 overflow-auto">
          {loading.data ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : !selectedTable ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Database className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Select a table from the sidebar to view its data</p>
            </div>
          ) : tableData.rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Table2 className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">No data in this table</p>
              <Button onClick={handleOpenCreate} className="gap-2" data-testid="add-first-record-btn">
                <Plus className="w-4 h-4" />
                Add First Record
              </Button>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedRows.size === tableData.rows.length && tableData.rows.length > 0}
                      onCheckedChange={toggleAllRows}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  {tableData.columns.map(col => (
                    <TableHead key={col} className="whitespace-nowrap">
                      {col}
                    </TableHead>
                  ))}
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.rows.map((row, idx) => (
                  <TableRow 
                    key={idx} 
                    data-testid={`data-row-${idx}`}
                    className={selectedRows.has(idx) ? 'bg-primary/5' : ''}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedRows.has(idx)}
                        onCheckedChange={() => toggleRowSelection(idx)}
                        data-testid={`select-row-${idx}`}
                      />
                    </TableCell>
                    {tableData.columns.map(col => (
                      <TableCell key={col} className="font-mono text-xs max-w-xs truncate">
                        {typeof row[col] === 'object' 
                          ? JSON.stringify(row[col]) 
                          : String(row[col] ?? '')}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(row, idx)}
                          data-testid={`edit-row-btn-${idx}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDelete(row, idx)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-row-btn-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Insufficient Credits Banner */}
        {tableData.insufficient_credits && (
          <div className="px-4 py-2 border-t border-red-500/30 bg-gradient-to-r from-red-500/10 to-transparent flex items-center justify-between" data-testid="no-credits-banner">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-500">Insufficient credits — <strong>Buy credits to browse data</strong></span>
            </div>
            <Button size="sm" className="gap-1.5 bg-primary hover:bg-primary/90" onClick={() => navigate('/subscribe')} data-testid="buy-credits-btn">
              <Zap className="w-3.5 h-3.5" />
              Buy Credits
            </Button>
          </div>
        )}

        {/* Pagination */}
        {selectedTable && tableData.total_count > 0 && (
          <footer className="h-12 border-t border-border bg-card/50 flex items-center justify-between px-4">
            <div className="text-sm text-muted-foreground">
              Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, tableData.total_count)} of {tableData.total_count.toLocaleString()}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.offset === 0}
                onClick={() => fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset - pagination.limit)}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.offset + pagination.limit >= tableData.total_count}
                onClick={() => fetchTableData(selectedConnection.id, selectedTable, pagination.limit, pagination.offset + pagination.limit)}
                data-testid="next-page-btn"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </footer>
        )}
      </div>

      {/* CREATE Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add New Record</DialogTitle>
            <DialogDescription>
              Create a new record in {selectedTable}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4 py-4">
              {editableColumns.map(col => (
                <div key={col.name} className="space-y-2">
                  <Label htmlFor={`create-${col.name}`} className="flex items-center gap-2">
                    {col.name}
                    <span className="text-xs text-muted-foreground">({col.data_type})</span>
                  </Label>
                  {col.data_type === 'object' || col.data_type === 'dict' || col.data_type === 'list' ? (
                    <Textarea
                      id={`create-${col.name}`}
                      value={formData[col.name] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [col.name]: e.target.value }))}
                      placeholder={`Enter ${col.data_type} as JSON`}
                      className="font-mono text-sm min-h-20"
                      data-testid={`create-field-${col.name}`}
                    />
                  ) : (
                    <Input
                      id={`create-${col.name}`}
                      value={formData[col.name] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [col.name]: e.target.value }))}
                      placeholder={`Enter ${col.data_type}`}
                      data-testid={`create-field-${col.name}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="confirm-create-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Record
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Record</DialogTitle>
            <DialogDescription>
              Update record in {selectedTable}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4 py-4">
              {columns.map(col => (
                <div key={col.name} className="space-y-2">
                  <Label htmlFor={`edit-${col.name}`} className="flex items-center gap-2">
                    {col.name}
                    <span className="text-xs text-muted-foreground">({col.data_type})</span>
                    {(col.name === '_id' || col.is_primary) && (
                      <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">Read-only</span>
                    )}
                  </Label>
                  {col.data_type === 'object' || col.data_type === 'dict' || col.data_type === 'list' ? (
                    <Textarea
                      id={`edit-${col.name}`}
                      value={formData[col.name] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [col.name]: e.target.value }))}
                      disabled={col.name === '_id' || col.is_primary}
                      className="font-mono text-sm min-h-20"
                      data-testid={`edit-field-${col.name}`}
                    />
                  ) : (
                    <Input
                      id={`edit-${col.name}`}
                      value={formData[col.name] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [col.name]: e.target.value }))}
                      disabled={col.name === '_id' || col.is_primary}
                      data-testid={`edit-field-${col.name}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={saving} data-testid="confirm-update-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Update Record
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DELETE Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive hover:bg-destructive/90"
              disabled={saving}
              data-testid="confirm-delete-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* BULK DELETE Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Records</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedRows.size} record(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive hover:bg-destructive/90"
              disabled={saving}
              data-testid="confirm-bulk-delete-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete {selectedRows.size} Records
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* FIELD TYPE EDIT Dialog */}
      <Dialog open={fieldEditDialogOpen} onOpenChange={setFieldEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Columns className="w-5 h-5" />
              Edit Field: {selectedField?.name}
            </DialogTitle>
            <DialogDescription>
              Change the data type for this field. Note: Schema changes may require data migration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Field Name</Label>
              <Input value={selectedField?.name || ''} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>Current Type</Label>
              <Input value={selectedField?.data_type || ''} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>New Type</Label>
              <Select value={fieldNewType} onValueChange={setFieldNewType}>
                <SelectTrigger data-testid="field-type-select">
                  <SelectValue placeholder="Select new type" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableDataTypes().map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedField?.is_primary && (
              <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm text-yellow-600">
                <AlertCircle className="w-4 h-4" />
                This is a primary key field. Changing its type may affect data integrity.
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setFieldEditDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleSaveFieldType} 
              disabled={savingField || fieldNewType === selectedField?.data_type}
              data-testid="save-field-type-btn"
            >
              {savingField ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ADD FIELD Dialog */}
      <Dialog open={addFieldDialogOpen} onOpenChange={setAddFieldDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add New Field to {selectedTable}
            </DialogTitle>
            <DialogDescription>
              Add a new column or field to the selected table.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-field-name">Field Name</Label>
              <Input
                id="new-field-name"
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="Enter field name"
                data-testid="new-field-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select value={newFieldType} onValueChange={setNewFieldType}>
                <SelectTrigger data-testid="new-field-type-select">
                  <SelectValue placeholder="Select field type" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableDataTypes().map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-field-default">Default Value (optional)</Label>
              <Input
                id="new-field-default"
                value={newFieldDefault}
                onChange={(e) => setNewFieldDefault(e.target.value)}
                placeholder="Enter default value"
                data-testid="new-field-default-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="new-field-nullable"
                checked={newFieldNullable}
                onCheckedChange={setNewFieldNullable}
                data-testid="new-field-nullable-checkbox"
              />
              <Label htmlFor="new-field-nullable" className="text-sm cursor-pointer">
                Allow NULL values
              </Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAddFieldDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddField}
              disabled={savingNewField || !newFieldName.trim() || !newFieldType}
              data-testid="confirm-add-field-btn"
            >
              {savingNewField ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Field
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DELETE FIELD Confirmation Dialog */}
      <AlertDialog open={deleteFieldDialogOpen} onOpenChange={setDeleteFieldDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Field: {fieldToDelete?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the field <strong>{fieldToDelete?.name}</strong> ({fieldToDelete?.data_type}) from <strong>{selectedTable}</strong>? This will remove the column and all its data permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteField}
              className="bg-destructive hover:bg-destructive/90"
              disabled={deletingField}
              data-testid="confirm-delete-field-btn"
            >
              {deletingField ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Field
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DatabaseExplorerPage;
